import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { formatCurrency, formatDate } from '../../../utils/localization';

const ExpenseList = ({ expenses, participants, onEditExpense, onDeleteExpense }) => {
  const [sortBy, setSortBy] = useState('date');
  const [filterBy, setFilterBy] = useState('all');

  const sortOptions = [
    { value: 'date', label: 'Date (Newest)' },
    { value: 'date-asc', label: 'Date (Oldest)' },
    { value: 'amount', label: 'Amount (High to Low)' },
    { value: 'amount-asc', label: 'Amount (Low to High)' },
    { value: 'description', label: 'Description (A-Z)' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Expenses' },
    ...participants?.map(p => ({ value: p?.id, label: `Paid by ${p?.name}` }))
  ];

  const sortedAndFilteredExpenses = expenses?.filter(expense => {
      if (filterBy === 'all') return true;
      return expense?.paidBy?.id === filterBy;
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.date) - new Date(a.date);
        case 'date-asc':
          return new Date(a.date) - new Date(b.date);
        case 'amount':
          return b?.amount - a?.amount;
        case 'amount-asc':
          return a?.amount - b?.amount;
        case 'description':
          return a?.description?.localeCompare(b?.description);
        default:
          return 0;
      }
    });

  const getSplitText = (expense, allParticipants) => {
    if (expense?.splitType === 'equal') {
      return `Split equally among ${allParticipants?.length} people`;
    } else {
      return 'Custom split';
    }
  };

  if (expenses?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Receipt" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No expenses yet</h3>
        <p className="text-muted-foreground mb-4">
          Start by adding your first expense to track group spending
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header with filters */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-lg font-semibold text-foreground">Expenses</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select
              placeholder="Filter by payer"
              options={filterOptions}
              value={filterBy}
              onChange={setFilterBy}
              className="w-full sm:w-48"
            />
            <Select
              placeholder="Sort by"
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              className="w-full sm:w-48"
            />
          </div>
        </div>
      </div>
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Description</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Amount</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Paid by</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Split</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredExpenses?.map((expense, index) => (
              <tr
                key={expense?.id}
                className={`border-b border-border ${index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}
              >
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Receipt" size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{expense?.description}</p>
                      {expense?.category && (
                        <p className="text-xs text-muted-foreground">{expense?.category}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="font-semibold text-foreground">{formatCurrency(expense?.amount)}</span>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-foreground">
                        {expense?.paidBy?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm text-foreground">{expense?.paidBy?.name}</span>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">{getSplitText(expense, participants)}</span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">{formatDate(expense?.date)}</span>
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onEditExpense(expense)}
                      className="text-muted-foreground hover:text-foreground"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onDeleteExpense(expense)}
                      className="text-muted-foreground hover:text-destructive"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden">
        {sortedAndFilteredExpenses?.map((expense) => (
          <div key={expense?.id} className="p-4 border-b border-border last:border-b-0">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Receipt" size={16} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">{expense?.description}</p>
                  <p className="text-sm text-muted-foreground">{formatDate(expense?.date)}</p>
                </div>
              </div>
              <span className="font-semibold text-foreground">{formatCurrency(expense?.amount)}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-primary-foreground">
                    {expense?.paidBy?.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">
                  Paid by {expense?.paidBy?.name}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Edit"
                  onClick={() => onEditExpense(expense)}
                  className="text-muted-foreground hover:text-foreground"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => onDeleteExpense(expense)}
                  className="text-muted-foreground hover:text-destructive"
                />
              </div>
            </div>
            
            <div className="mt-2 pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">{getSplitText(expense, participants)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExpenseList;
